// src/common/hooks/useItemInteractions.ts (веб)
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleFavorite as toggleFavoriteAction,
  markAsViewed,
  addRating,
  removeRating as removeRatingAction,
  selectSaleItem,
} from "../../store/slices/saleItemListSlice";
import {
  addFavoriteItem,
  removeFavoriteItem,
} from "../../store/slices/favoritesSlice";
import { addPendingAction, SyncActionType } from "../../store/slices/syncSlice";
import { RatingValue } from "../types/enums";
import { selectCurrentUser } from "../../store/slices/currentUserSlice";
import { v4 } from "uuid";
import { store } from "../../store/store";
import { subHours } from "date-fns";

export const useItemInteractions = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);

  // === 1. ИЗБРАННОЕ ===
  const toggleFavorite = useCallback(
    (itemId: number) => {
      if (!currentUser?.id) return { success: false, localId: undefined };

      // Получаем текущий статус
      const item = selectSaleItem(store.getState(), itemId);
      const isFavorite =
        item?.selected?.some(
          (sel) => sel.userId === currentUser.id && sel.isActive,
        ) ?? false;

      const localId = v4();

      // 1. Оптимистичное обновление UI в saleItemListSlice
      dispatch(toggleFavoriteAction({ itemId, userId: currentUser.id }));

      // 2. Синхронизация с favoritesSlice
      if (isFavorite) {
        dispatch(removeFavoriteItem(itemId));
      } else {
        if (item) {
          const now = new Date().toISOString();
          const newFavorite = {
            id: v4(),
            userId: currentUser.id,
            itemId: item.id!,
            selectedAt: now,
            isActive: true,
          };
          const updatedSelected = item.selected
            ? [...item.selected, newFavorite]
            : [newFavorite];

          const favoriteItem = {
            ...item,
            selected: updatedSelected,
            totalFavorites: (Number(item.totalFavorites) || 0) + 1,
          };
          dispatch(addFavoriteItem(favoriteItem));
        }
      }

      // 3. Добавляем в очередь на отправку
      dispatch(
        addPendingAction({
          type: SyncActionType.FAVORITE,
          itemId: itemId,
          userId: currentUser.id,
        }),
      );

      return { success: true, localId };
    },
    [dispatch, currentUser?.id],
  );

  const trackView = useCallback(
    (itemId: number) => {
      if (!currentUser?.id) return { success: false, localId: undefined };

      const item = selectSaleItem(store.getState(), itemId);

      if (item) {
        const twentyFourHoursAgo = subHours(new Date(), 24);
        const hasRecentView = item.views?.some(
          (view) =>
            view.userId === currentUser.id &&
            new Date(view.viewedAt) > twentyFourHoursAgo,
        );

        if (hasRecentView) {
          return { success: false, localId: undefined };
        }
      }

      const localId = v4();

      dispatch(markAsViewed({ itemId, userId: currentUser.id }));

      dispatch(
        addPendingAction({
          type: SyncActionType.VIEW,
          itemId: itemId,
          userId: currentUser.id,
        }),
      );

      return { success: true, localId };
    },
    [dispatch, currentUser?.id],
  );

  // === 3. ОЦЕНКИ ===
  const rateItem = useCallback(
    (itemId: number, rating: number, comment?: string) => {
      if (!currentUser?.id) return { success: false, localId: undefined };

      const localId = v4();
      const apiRating = (rating - 1) as RatingValue;

      dispatch(
        addRating({
          itemId,
          userId: currentUser.id,
          rating: apiRating,
          comment,
        }),
      );

      dispatch(
        addPendingAction({
          type: SyncActionType.RATING,
          itemId: itemId,
          userId: currentUser.id,
          data: {
            rating: apiRating,
            comment: comment,
          },
        }),
      );

      return { success: true, localId };
    },
    [dispatch, currentUser?.id],
  );

  // === 4. УДАЛЕНИЕ ОЦЕНКИ ===
  const removeRating = useCallback(
    (itemId: number) => {
      if (!currentUser?.id) return { success: false, localId: undefined };

      const localId = v4();

      dispatch(removeRatingAction({ itemId, userId: currentUser.id }));

      dispatch(
        addPendingAction({
          type: SyncActionType.RATING,
          itemId: itemId,
          userId: currentUser.id,
          data: {
            rating: 0 as RatingValue,
          },
        }),
      );

      return { success: true, localId };
    },
    [dispatch, currentUser?.id],
  );

  // === 5. ПАКЕТНЫЕ ПРОСМОТРЫ ===
  const trackViewsBatch = useCallback(
    (itemIds: number[]) => {
      if (!currentUser?.id || itemIds.length === 0) {
        return { success: false, count: 0 };
      }

      const localIds: string[] = [];

      itemIds.forEach((itemId) => {
        const localId = v4();

        dispatch(markAsViewed({ itemId, userId: Number(currentUser.id) }));

        dispatch(
          addPendingAction({
            type: SyncActionType.VIEW,
            itemId: itemId,
            userId: Number(currentUser.id),
          }),
        );

        localIds.push(localId);
      });

      return { success: true, count: itemIds.length, localIds };
    },
    [dispatch, currentUser?.id],
  );

  // === 6. ПРОВЕРКА ВОЗМОЖНОСТИ ОЦЕНКИ ===
  const canRateItem = useCallback(
    (
      itemId: number,
      purchasedMap: Map<number, boolean>,
      itemRatings?: any[],
    ) => {
      if (!currentUser?.id) return false;

      const hasPurchased = purchasedMap.get(itemId) || false;
      if (!hasPurchased) return false;

      const hasRated = itemRatings?.some(
        (rating) => rating.userId === currentUser.id,
      );
      if (hasRated) return false;

      return true;
    },
    [currentUser],
  );

  return {
    toggleFavorite,
    trackView,
    rateItem,
    removeRating,
    trackViewsBatch,
    canRateItem,
    currentUserId: currentUser?.id,
  };
};
